import { useState, useMemo, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PipelineLoan, getBorrowerName, getLoanOfficerName } from '../types';
import { getLoanTypeCategory, getNonQmSubtype } from './fundedSummaryModel';
import { format, parseISO, differenceInDays } from 'date-fns';

interface FundedDataTableProps {
  loans: PipelineLoan[];
  isLoading: boolean;
  getStrictFundedDate: (loan: PipelineLoan) => string | null;
}

type SortField =
  | 'loan_number'
  | 'borrower'
  | 'lo'
  | 'amount'
  | 'rate'
  | 'investor'
  | 'channel'
  | 'loan_type'
  | 'doc_type'
  | 'product'
  | 'funded_date'
  | 'purchased_date'
  | 'days_in_warehouse';
type SortDirection = 'asc' | 'desc';

// Doc-type subtype shown only for Non-QM loans (blank for everything else).
function getDocTypeDisplay(loan: PipelineLoan): string {
  return getLoanTypeCategory(loan) === 'Non-QM' ? getNonQmSubtype(loan) : '';
}

export function FundedDataTable({ loans, isLoading, getStrictFundedDate }: FundedDataTableProps) {
  const [sortField, setSortField] = useState<SortField>('funded_date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getDaysInWarehouse = useCallback(
    (loan: PipelineLoan): number | null => {
      const fundedDate = getStrictFundedDate(loan);
      if (!fundedDate || !loan.purchased_date) {
        return null;
      }
      try {
        return differenceInDays(parseISO(loan.purchased_date), parseISO(fundedDate));
      } catch {
        return null;
      }
    },
    [getStrictFundedDate]
  );

  const sortedLoans = useMemo(() => {
    return [...loans].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'loan_number':
          comparison = (a.trans_details_loan_number || '').localeCompare(b.trans_details_loan_number || '');
          break;
        case 'borrower':
          comparison = getBorrowerName(a).localeCompare(getBorrowerName(b));
          break;
        case 'lo':
          comparison = getLoanOfficerName(a).localeCompare(getLoanOfficerName(b));
          break;
        case 'amount':
          comparison = (a.trans_details_loan_amt || 0) - (b.trans_details_loan_amt || 0);
          break;
        case 'rate':
          comparison = (a.trans_details_interest_rate || 0) - (b.trans_details_interest_rate || 0);
          break;
        case 'investor':
          comparison = (a.investor_name || '').localeCompare(b.investor_name || '');
          break;
        case 'channel':
          comparison = (a.channel || '').localeCompare(b.channel || '');
          break;
        case 'loan_type':
          comparison = getLoanTypeCategory(a).localeCompare(getLoanTypeCategory(b));
          break;
        case 'doc_type':
          comparison = getDocTypeDisplay(a).localeCompare(getDocTypeDisplay(b));
          break;
        case 'product':
          comparison = (a.rate_lock_loan_program || '').localeCompare(b.rate_lock_loan_program || '');
          break;
        case 'funded_date':
          comparison = (getStrictFundedDate(a) || '').localeCompare(getStrictFundedDate(b) || '');
          break;
        case 'purchased_date':
          comparison = (a.purchased_date || '').localeCompare(b.purchased_date || '');
          break;
        case 'days_in_warehouse':
          comparison = (getDaysInWarehouse(a) || 0) - (getDaysInWarehouse(b) || 0);
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [loans, sortField, sortDirection, getStrictFundedDate, getDaysInWarehouse]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) {
return '-';
}
    try {
      return format(parseISO(dateStr), 'MM/dd/yy');
    } catch {
      return '-';
    }
  };

  const formatCurrency = (amount: number | null) => {
    if (amount === null || amount === undefined) {
return '-';
}
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatRate = (rate: number | null) => {
    if (rate === null || rate === undefined) {
return '-';
}
    return `${rate.toFixed(3)}%`;
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-1 h-3 w-3" />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="ml-1 h-3 w-3" />
    ) : (
      <ArrowDown className="ml-1 h-3 w-3" />
    );
  };

  const SortableHeader = ({
    field,
    children
  }: {
    field: SortField;
    children: React.ReactNode;
  }) => (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 data-[state=open]:bg-accent"
      onClick={() => handleSort(field)}
    >
      {children}
      <SortIcon field={field} />
    </Button>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (loans.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No funded loans found for the selected criteria.
      </div>
    );
  }

  return (
    <div className="rounded-md border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              <SortableHeader field="loan_number">Loan #</SortableHeader>
            </TableHead>
            <TableHead>
              <SortableHeader field="borrower">Borrower</SortableHeader>
            </TableHead>
            <TableHead>
              <SortableHeader field="lo">LO</SortableHeader>
            </TableHead>
            <TableHead className="text-right">
              <SortableHeader field="amount">Amount</SortableHeader>
            </TableHead>
            <TableHead className="text-right">
              <SortableHeader field="rate">Rate</SortableHeader>
            </TableHead>
            <TableHead>
              <SortableHeader field="investor">Investor</SortableHeader>
            </TableHead>
            <TableHead>
              <SortableHeader field="channel">Channel</SortableHeader>
            </TableHead>
            <TableHead>
              <SortableHeader field="loan_type">Loan Type</SortableHeader>
            </TableHead>
            <TableHead>
              <SortableHeader field="doc_type">Doc Type</SortableHeader>
            </TableHead>
            <TableHead>
              <SortableHeader field="product">Product</SortableHeader>
            </TableHead>
            <TableHead>
              <SortableHeader field="funded_date">Funded Date</SortableHeader>
            </TableHead>
            <TableHead>
              <SortableHeader field="purchased_date">Purchased</SortableHeader>
            </TableHead>
            <TableHead className="text-right">
              <SortableHeader field="days_in_warehouse">Whse Days</SortableHeader>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedLoans.map((loan) => {
            const days = getDaysInWarehouse(loan);
            return (
              <TableRow key={loan.encompass_loan_id}>
                <TableCell className="font-medium">
                  {loan.trans_details_loan_number || '-'}
                </TableCell>
                <TableCell className="max-w-[150px] truncate">
                  {getBorrowerName(loan)}
                </TableCell>
                <TableCell className="max-w-[120px] truncate">
                  {getLoanOfficerName(loan)}
                </TableCell>
                <TableCell className="text-right">
                  {formatCurrency(loan.trans_details_loan_amt)}
                </TableCell>
                <TableCell className="text-right">
                  {formatRate(loan.trans_details_interest_rate)}
                </TableCell>
                <TableCell className="max-w-[150px] truncate">
                  {(loan.investor_name || '').trim() || 'Other'}
                </TableCell>
                <TableCell>
                  {loan.channel?.includes('Brokered') ? (
                    <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-800">Brokered</Badge>
                  ) : loan.channel ? (
                    <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-800">Banked</Badge>
                  ) : '-'}
                </TableCell>
                <TableCell className="text-xs">
                  {getLoanTypeCategory(loan)}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {getDocTypeDisplay(loan) || '—'}
                </TableCell>
                <TableCell className="max-w-[150px] truncate">
                  {loan.rate_lock_loan_program || '-'}
                </TableCell>
                <TableCell className="font-medium">
                  {formatDate(getStrictFundedDate(loan))}
                </TableCell>
                <TableCell>
                  {loan.purchased_date ? (
                    <Badge variant="secondary" className="text-xs bg-green-100 text-green-800">
                      {formatDate(loan.purchased_date)}
                    </Badge>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {days !== null ? days : '-'}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
